
import React, { useState, useEffect } from "react";
import Layout from "./Layout";
import { useAuth } from "../contexts/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaPlus,
  FaTrash,
  FaClock,
  FaComments,
  FaChevronRight,
} from "react-icons/fa";
import "./MainPage.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function MainPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchQuery, setSearchQuery] = useState("");
  const [miniShots, setMiniShots] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (location.state?.openMiniShot) {
      const newId =
        location.state.openMiniShot.id || location.state.openMiniShot._id;
      navigate(`/minishot/${newId}`);
    }
  }, [location.state, navigate]);

  useEffect(() => {
    let ignore = false;
    const load = async () => {
      try {
        setIsLoading(true);
        const res = await fetch(`${API_BASE_URL}/api/minishots`, {
          credentials: "include",
        });
        if (!ignore && res.ok) {
          const data = await res.json();
          setMiniShots(data.miniShots || []);
        }
      } catch (err) {
        console.error("Error loading minishots:", err);
      } finally {
        !ignore && setIsLoading(false);
      }
    };
    load();
    return () => {
      ignore = true;
    };
  }, []);

  const startNewChat = () => navigate("/moonshot");

  const deleteMiniShot = async (id, title) => {
    if (!window.confirm(`Delete "${title}" ?`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/minishots/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        setMiniShots((prev) => prev.filter((ms) => ms.id !== id));
      } else {
        alert("Failed to delete");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const formatTime = (ts) => {
    const h = (Date.now() - new Date(ts)) / 36e5;
    if (h < 1) return "Just now";
    if (h < 24) return `${Math.floor(h)}h ago`;
    if (h < 24 * 7) return `${Math.floor(h / 24)}d ago`;
    return new Date(ts).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  };

  const filtered = miniShots.filter(
    (m) =>
      m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Layout searchQuery={searchQuery} setSearchQuery={setSearchQuery}>
      <div className="welcome-section">
        <div className="welcome-content">
          <h1 className="main-title">
            Hi{" "}
            <span className="highlight">{currentUser?.name || "Creator"}</span>!
          </h1>
          <p className="main-subtitle">
            Your Moonshots — Keep your strategic vision clear, structured, and
            on track.
          </p>
        </div>
        <div className="welcome-actions">
          <button className="new-moonshot-btn" onClick={startNewChat}>
            <FaPlus /> <span>New Moonshots</span>
          </button>
        </div>
      </div>

      <div className="minishots-section">
        <div className="minishots-header">
          <h2>My Moonshoots</h2>
        </div>

        <div className="minishots-grid">
          {isLoading ? (
            <div className="loading-state">
              <div className="loading-spinner" />
              <p>Loading your chats…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <FaComments className="empty-icon" />
              <h3>No chats yet</h3>
              <p>
                Click "New Minishot" above to start your first conversation.
              </p>
            </div>
          ) : (
            filtered.map((ms) => (
              <div
                key={ms.id || ms._id}
                className="minishot-card"
                onClick={() => navigate(`/minishot/${ms.id || ms._id}`)}
              >
                {/* TOP SECTION: Header Info + Title + Desc */}
                <div className="card-top">
                  <div className="card-header-row">
                    <span className="card-date">
                      <FaClock style={{ fontSize: "0.75rem" }} />{" "}
                      {formatTime(ms.lastMessageAt)}
                    </span>
                    <button
                      className="delete-btn-modern"
                      title="Delete Project"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteMiniShot(ms.id || ms._id, ms.title);
                      }}
                    >
                      <FaTrash />
                    </button>
                  </div>

                  <h3 className="card-title">{ms.title}</h3>
               
                </div>

                {/* BOTTOM SECTION: Stats + Action Icon */}
                <div className="card-bottom">
                  <div className="meta-stats">
                    <span className="stat-pill">
                      <FaComments /> {ms.messageCount || 0}
                    </span>
                  </div>
                  <div className="card-action-arrow">
                    Open <FaChevronRight size={10} />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}
